import { useId } from 'react'
import { Button } from '@/components/ui/Button'
import { toggleClass } from '@/components/ui/toggle'
import { cn } from '@/lib/cn'
import { usePrintPeriod } from '@/lib/printMeta'

export interface Range {
  from: string // ISO yyyy-mm-dd, '' = unset
  to: string
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** The range a preset of `days` stands for (the same dates `applyPreset` sends). */
export function presetRange(days: number, today?: string): Range {
  if (today) {
    const from = new Date(`${today}T00:00:00Z`)
    from.setUTCDate(from.getUTCDate() - (days - 1))
    return { from: iso(from), to: today }
  }
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - (days - 1))
  return { from: iso(from), to: iso(to) }
}

const FIELD = cn(
  'h-10 min-w-0 flex-1 rounded-sm sm:flex-none border border-field bg-surface-3 px-2 text-body-lg text-ink-1 md:text-body',
  'focus-visible:border-cyan focus-visible:shadow-glow-cyan focus-visible:outline-none',
  '[@media(pointer:coarse)]:h-11',
)

/** Presets + two native date inputs. Presets fill concrete from/to so the caller always
 *  sends an explicit range. Empty range = no date filter. `today` (YYYY-MM-DD) anchors the
 *  presets to a report's own calendar — «Привлечение» counts Moscow days; without it the
 *  presets end on today's UTC date as before.
 *  The preset that matches the range shows it (`aria-pressed`, the selected toggle look, ✓ —
 *  WP-14); every control is 40 px, the height of a FilterBar row. `label` puts a visible
 *  caption above, like `Select`. */
export function DateRange({
  value,
  onChange,
  presets = [7, 14, 30, 90],
  today,
  label,
  className,
}: {
  value: Range
  onChange: (r: Range) => void
  presets?: number[]
  today?: string
  /** «Даты создания» — a visible caption above (the group is named by it) */
  label?: string
  className?: string
}) {
  const id = useId()
  // On paper the controls go (print:hidden) and the period moves into the shell's stamp line
  // «Сформировано …, период 01.09.2026 — 30.09.2026» (WP-25).
  usePrintPeriod(value.from || value.to ? `период ${rangeText(value)}` : 'за всё время')
  return (
    <div className={cn('min-w-0 print:hidden', className)} role="group" aria-labelledby={label ? id : undefined} aria-label={label ? undefined : 'Период'}>
      {label && (
        <span id={id} className="mb-1.5 block text-caption text-ink-2">
          {label}
        </span>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {presets.map((d) => {
            const r = presetRange(d, today)
            const on = value.from === r.from && value.to === r.to
            return (
              <button
                key={d}
                type="button"
                aria-pressed={on}
                title={`Последние ${d} дней`}
                className={toggleClass(on, 'md')}
                onClick={() => onChange(r)}
              >
                {on && <span aria-hidden>✓</span>}
                {d}д
              </button>
            )
          })}
        </div>
        <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto">
          <input
            type="date"
            aria-label="С даты"
            value={value.from}
            max={value.to || undefined}
            onChange={(e) => onChange({ ...value, from: e.target.value })}
            className={FIELD}
          />
          <span aria-hidden className="text-ink-3">
            —
          </span>
          <input
            type="date"
            aria-label="По дату"
            value={value.to}
            min={value.from || undefined}
            onChange={(e) => onChange({ ...value, to: e.target.value })}
            className={FIELD}
          />
        </div>
        {(value.from || value.to) && (
          <Button size="sm" variant="ghost" className="h-10" onClick={() => onChange({ from: '', to: '' })}>
            Сбросить
          </Button>
        )}
      </div>
    </div>
  )
}

/** «01.09.2026 — 27.09.2026» for a filter chip: the calendar dates as typed (no time zone). */
export function rangeText(r: Range): string {
  const d = (v: string) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? v.split('-').reverse().join('.') : '…')
  return `${d(r.from)} — ${d(r.to)}`
}

/** Build the &date_from=&date_to= query fragment from a range (empty parts omitted). */
export function rangeQuery(r: Range): string {
  const parts: string[] = []
  if (r.from) parts.push(`date_from=${r.from}`)
  if (r.to) parts.push(`date_to=${r.to}`)
  return parts.length ? `&${parts.join('&')}` : ''
}
